/**
 * Catalogo.tsx — el catálogo de cuentas, y lo poco que se deja tocar.
 *
 * La pantalla existe para que el contador adapte la plantilla: renombrar,
 * colgar auxiliares, desactivar lo que no use. Un catálogo que no se deja
 * tocar se abandona en la primera semana.
 *
 * ── Lo que esta pantalla NO ofrece, y por qué ─────────────────────────────
 *
 * No hay campo para cambiar el tipo, la naturaleza, la categoría NIIF 18 ni la
 * línea de negocio. No es que estén deshabilitados: **no están**. Un campo
 * apagado invita a preguntar cómo encenderlo; su ausencia dice que esa no es
 * una operación del producto. La base lo rechaza igual (SB033) — esto es que
 * nadie llegue hasta ahí.
 *
 * Tampoco hay botón de borrar una cuenta de plantilla. Se desactiva.
 *
 * ── Las cuentas desactivadas se ven ───────────────────────────────────────
 *
 * Atenuadas, pero se ven. Esconderlas haría que desactivar pareciera borrar, y
 * entonces nadie lo usaría por miedo — o peor, lo usaría creyendo que borra.
 *
 * ── El código de una cuenta nueva viene puesto ────────────────────────────
 *
 * El campo arranca con el código del padre. La regla —una cuenta cuelga dentro
 * del rango de su padre— se ve antes de escribir en vez de descubrirse en el
 * rechazo.
 *
 * ── ▶ LOS FILTROS SON DEL CLIENTE, Y NO ES POR PEREZA ─────────────────────
 *
 * El diario y el mayor filtran en la BASE porque la historia de una empresa no
 * cabe. Acá el catálogo **ya está traído entero** —vive en un contexto porque
 * lo comparten tres pantallas, y el formulario de asiento lo necesita completo
 * para poder ofrecer cualquier cuenta—, así que mandarlo al servidor sería una
 * segunda llamada por un dato que ya está en memoria, y dejaría al selector de
 * cuentas del asiento mirando un catálogo recortado.
 *
 * Eran 123 cuentas y ninguna forma de encontrar una. **El problema no era que
 * la consulta pesara: era que una persona no encontraba nada.** Es la misma
 * palabra para dos problemas distintos, y conviene no confundirlos al leer.
 *
 * La regla vive en `nucleo/buscar.ts`, con sus casos. Acá solo se usa.
 */
import { useMemo, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { puedeRegistrar, useSesion } from '../nucleo/sesion.tsx';
import { nombreDe, useCatalogo } from '../nucleo/catalogo.tsx';
import {
  filtrarCuentas, hayCriterio, SIN_CRITERIO, type CriterioDeCuentas,
} from '../nucleo/buscar.ts';
import {
  activarCuenta, agregarCuenta, renombrarCuenta, FalloDeApi,
  type CuentaReal,
} from '../datos/libros.ts';

/**
 * Los grupos que se pueden pedir.
 *
 * Salen de la restricción `tipo_conocido` de la 0002 y son los seis que la
 * base admite. `mixed` va también: existe en el catálogo —el diferencial
 * cambiario— y omitirlo dejaría cuentas imposibles de aislar.
 */
const GRUPOS = ['asset', 'liability', 'equity', 'income', 'expense', 'mixed'] as const;

/** Profundidad por la CADENA de padres, no por el largo del código. */
function profundidades(cuentas: readonly CuentaReal[]): Map<string, number> {
  const porCodigo = new Map(cuentas.map((c) => [c.codigo, c]));
  const cache = new Map<string, number>();

  const de = (c: CuentaReal): number => {
    const visto = cache.get(c.codigo);
    if (visto !== undefined) return visto;
    // Los códigos van 1 → 11 → 1101 → 1101-01: el largo salta de 2 a 4, así
    // que medir con `length` daría una sangría que miente. La cadena de padres
    // es la estructura de verdad.
    const padre = c.padre ? porCodigo.get(c.padre) : undefined;
    const n = padre ? de(padre) + 1 : 0;
    cache.set(c.codigo, n);
    return n;
  };

  for (const c of cuentas) de(c);
  return cache;
}

type Modo =
  | { que: 'nada' }
  | { que: 'renombrar'; cuenta: CuentaReal }
  | { que: 'colgar'; padre: CuentaReal };

export function Catalogo() {
  const { t, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();
  const { cuentas, cargando, fallo, recargar } = useCatalogo();

  const edita = puedeRegistrar(sesion);
  const [modo, setModo] = useState<Modo>({ que: 'nada' });
  const [falloClave, setFalloClave] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [nombre, setNombre] = useState('');
  const [nombreEn, setNombreEn] = useState('');
  const [codigo, setCodigo] = useState('');

  const [criterio, setCriterio] = useState<CriterioDeCuentas>(SIN_CRITERIO);
  const pon = (cambio: Partial<CriterioDeCuentas>) =>
    setCriterio((c) => ({ ...c, ...cambio }));

  // ▶ La sangría se mide sobre el catálogo COMPLETO, no sobre lo filtrado.
  //
  // Es lo que hace que `1101-01` siga dibujándose tres niveles adentro cuando
  // la búsqueda lo trae solo con sus padres. Calcularla sobre `visibles` daría
  // una profundidad que cambia con lo que se escribe en un campo de texto — y
  // la sangría de este árbol no es decoración: es la estructura de la que
  // dependen los estados.
  const hondo = useMemo(() => profundidades(cuentas), [cuentas]);

  const filtrado = useMemo(
    () => filtrarCuentas(cuentas, criterio), [cuentas, criterio]);
  const filtrando = hayCriterio(criterio);

  /**
   * Cuáles tienen hijas ACTIVAS.
   *
   * Se calcula acá para no ofrecer «desactivar» sobre una cuenta que la base va
   * a rechazar (SB035). Es la misma razón por la que esta pantalla no tiene un
   * campo apagado para la naturaleza: ofrecer algo que no se puede hacer manda
   * a la persona a averiguar por qué falló, cuando la respuesta era «esto no
   * era una operación».
   *
   * La base sigue siendo quien decide — esto solo evita llegar hasta ahí.
   */
  const conHijasActivas = useMemo(() => {
    const con = new Set<string>();
    for (const c of cuentas) if (c.activa && c.padre) con.add(c.padre);
    return con;
  }, [cuentas]);

  function abrirRenombrar(c: CuentaReal) {
    setFalloClave(null);
    setNombre(c.nombre);
    setNombreEn(c.nombreEn);
    setModo({ que: 'renombrar', cuenta: c });
  }

  function abrirColgar(c: CuentaReal) {
    setFalloClave(null);
    setNombre('');
    setNombreEn('');
    // Puesto de entrada: la cuenta nueva vive dentro del rango de su padre.
    setCodigo(`${c.codigo}-`);
    setModo({ que: 'colgar', padre: c });
  }

  async function correr(accion: () => Promise<void>) {
    setOcupado(true);
    setFalloClave(null);
    try {
      await accion();
      setModo({ que: 'nada' });
      recargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFalloClave(
        f?.clase === 'conexion' ? 'sb.conexion'
          : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{t('cat.title')}</h1>
          <p className="page-head__sub">{t('cat.sub')}</p>
        </div>
      </div>

      {!edita && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(sesion?.capacidad === 'piso' ? 'acc.floorWhy' : 'acc.readOnly')}</span>
        </div>
      )}

      {falloClave && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(falloClave as never)}</span>
        </div>
      )}

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t('sb.conexion')}</span>
        </div>
      )}

      {/* ── El formulario, arriba y no en un diálogo ── */}
      {modo.que !== 'nada' && (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          <div className="label" style={{ marginBottom: 'var(--s6)' }}>
            <span>
              {modo.que === 'renombrar'
                ? `${t('cat.rename')} · ${modo.cuenta.codigo}`
                : `${t('cat.addChild')} · ${modo.padre.codigo} ${nombreDe(modo.padre, idioma)}`}
            </span>
          </div>

          {modo.que === 'colgar' && (
            <label className="field" style={{ marginBottom: 'var(--s5)' }}>
              <span className="field__label">{t('cat.newCode')}</span>
              <input
                className="input input--mono" value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
              />
              <span className="field__hint">{t('cat.inheritNote')}</span>
            </label>
          )}

          <div className="grid grid--2" style={{ gap: 'var(--s5)' }}>
            <label className="field">
              <span className="field__label">{t('cat.nameEs')}</span>
              <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">{t('cat.nameEn')}</span>
              <input className="input" value={nombreEn} onChange={(e) => setNombreEn(e.target.value)} />
            </label>
          </div>
          <p className="field__hint" style={{ marginTop: 'var(--s4)' }}>{t('cat.bothLangs')}</p>

          <div className="page-actions" style={{ marginTop: 'var(--s6)' }}>
            <button
              className="btn btn--ghost" type="button"
              onClick={() => setModo({ que: 'nada' })}
            >
              {t('cat.cancel')}
            </button>
            <button
              className="btn btn--primary" type="button"
              disabled={ocupado || nombre.trim() === ''
                || (modo.que === 'renombrar' && nombreEn.trim() === '')}
              onClick={() => void correr(async () => {
                if (modo.que === 'renombrar') {
                  await renombrarCuenta(pedir, {
                    codigo: modo.cuenta.codigo, nombre, nombreEn,
                  });
                } else {
                  // Sin inglés se manda vacío y la base copia el español. No se
                  // hereda el del padre: una cuenta que en español dice «Caja
                  // chica Heredia» y en inglés dice otra cosa es una cuenta
                  // distinta según quién la mire.
                  await agregarCuenta(pedir, {
                    padre: modo.padre.codigo, codigo, nombre, nombreEn: nombreEn || undefined,
                  });
                }
              })}
            >
              {t('cat.save')}
            </button>
          </div>
        </section>
      )}

      {/* ── Los filtros ── */}
      <section className="card filtros" style={{ marginBottom: 'var(--s6)' }}>
        <label className="field field--inline filtros__buscar">
          <span className="field__label">{t('cat.search')}</span>
          <input
            className="input" value={criterio.texto} placeholder={t('cat.searchHint')}
            onChange={(e) => pon({ texto: e.target.value })}
          />
        </label>

        <label className="field field--inline">
          <span className="field__label">{t('cat.group')}</span>
          <select
            className="select" value={criterio.tipo}
            onChange={(e) => pon({ tipo: e.target.value })}
          >
            <option value="">{t('cat.allGroups')}</option>
            {GRUPOS.map((g) => (
              <option key={g} value={g}>{t(`common.${g}` as never)}</option>
            ))}
          </select>
        </label>

        <label className="field field--inline">
          <span className="field__label">
            <input
              type="checkbox" checked={criterio.soloMovimiento}
              onChange={(e) => pon({ soloMovimiento: e.target.checked })}
            />
            {t('cat.onlyPostable')}
          </span>
        </label>

        {/* ▶ Esconder las desactivadas se PIDE, no viene puesto. La cabecera de
            este archivo dice por qué se ven: esconderlas haría que desactivar
            pareciera borrar. Un filtro que las escondiera por omisión deshace
            esa decisión sin que nadie la discuta. */}
        <label className="field field--inline">
          <span className="field__label">
            <input
              type="checkbox" checked={criterio.ocultarInactivas}
              onChange={(e) => pon({ ocultarInactivas: e.target.checked })}
            />
            {t('cat.hideInactive')}
          </span>
        </label>

        {filtrando && (
          <button
            className="btn btn--ghost btn--sm" type="button"
            onClick={() => setCriterio(SIN_CRITERIO)}
          >
            {t('cat.clearFilters')}
          </button>
        )}
      </section>

      {/* ── El árbol ── */}
      <section className="card card--pad0">
        <div className="card__head">
          <h3>{t('cat.title')}</h3>
          <span className="spacer" />
          <span className="small muted">
            {/* ▶ Con filtro puesto el número es OTRO, y tiene que decirlo.
                «8 cuentas» cuando hay 123 y coincidieron 2 es exactamente la
                familia del guardián 13: un número que se lee como otra cosa.
                Y son DOS números —los que coinciden y el total— porque «2
                cuentas» a secas se lee como «esta empresa tiene dos». */}
            {cargando
              ? t('common.loading')
              : filtrando
                ? t('cat.countFiltered', {
                  cuantas: String(filtrado.cuantas), total: String(cuentas.length),
                })
                : cuentas.length === 1
                  ? t('cat.countOne')
                  : t('cat.count', { cuentas: String(cuentas.length) })}
          </span>
        </div>

        <div className="table-wrap table-wrap--alto">
          <table className="lines">
            <tbody>
              {filtrado.visibles.map((c) => {
                /* ▶ Una fila que está por ser PADRE de algo que coincidió, no
                   porque coincidiera ella. Se marca porque si no, una búsqueda
                   de «caja chica» devolvería cuatro filas de las que tres no
                   tienen nada que ver — y quien mira no tiene cómo saber cuál
                   es la que buscaba. Sin filtro no hay contexto: todas son
                   ellas mismas. */
                const contexto = filtrando && !filtrado.coincidieron.has(c.codigo);
                return (
                <tr
                  key={c.codigo}
                  className={`${c.activa ? '' : 'cuenta--off'}${contexto ? ' cuenta--contexto' : ''}`}
                >
                  <td className="mono small" style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ paddingLeft: `${(hondo.get(c.codigo) ?? 0) * 18}px` }}>
                      {c.codigo}
                    </span>
                  </td>
                  <td>
                    {nombreDe(c, idioma)}
                    {!c.permiteMovimiento && (
                      <span className="chip chip--soft" style={{ marginLeft: 8 }}>
                        {t('cat.ledgerAccount')}
                      </span>
                    )}
                    {!c.activa && (
                      <span className="chip chip--warn" style={{ marginLeft: 8 }}>
                        {t('cat.inactive')}
                      </span>
                    )}
                    {/* Qué clasificación exige. Se enseña acá porque es lo que
                        el formulario de asiento va a pedir, y verlo antes evita
                        la sorpresa de un campo que aparece al elegir la cuenta. */}
                    {c.clasificacion.map((d) => (
                      <span key={d} className="chip chip--soft" style={{ marginLeft: 6 }}>
                        {t(`cls.${d}` as never)}
                      </span>
                    ))}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {edita && (
                      <>
                        <button
                          className="btn btn--ghost btn--sm" type="button"
                          onClick={() => abrirRenombrar(c)}
                        >
                          {t('cat.rename')}
                        </button>
                        <button
                          className="btn btn--ghost btn--sm" type="button"
                          onClick={() => abrirColgar(c)}
                        >
                          {t('cat.addChild')}
                        </button>
                        {!(c.activa && conHijasActivas.has(c.codigo)) && (
                          <button
                            className="btn btn--ghost btn--sm" type="button"
                            disabled={ocupado}
                            onClick={() => void correr(
                              () => activarCuenta(pedir, c.codigo, !c.activa))}
                          >
                            {t(c.activa ? 'cat.deactivate' : 'cat.activate')}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
              {/* ▶ «No encontré nada» tiene que decirse. Una tabla vacía sin
                  una palabra se lee igual que un fallo de carga — y acá, con
                  un filtro puesto, la respuesta correcta es que la búsqueda no
                  dio nada, no que el catálogo esté vacío. */}
              {!cargando && filtrando && filtrado.visibles.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted" style={{ padding: 'var(--s7)' }}>
                    {t('cat.noMatch')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
